/**
 * chatActionsConfirmFlows
 *
 * Colocated confirm-flow factory for `ChatActionsSheetContent`. Houses the three
 * near-duplicate confirm → mutate → side-effect handlers (block conversation /
 * block user / mute) so the content component stays UI-orchestration-only.
 * Behaviour is unchanged from the inline handlers; only the location moved.
 *
 * Flutter source of truth:
 *  - `_onBlockConversation` (chat_actions_bottom_sheet.dart:207-256)
 *  - `_onBlockUser` (chat_actions_bottom_sheet.dart:258-306)
 *  - `_onMuteConversation` (chat_actions_bottom_sheet.dart:308-358)
 *
 * The Flutter side-effect asymmetry between the flows is reproduced exactly
 * — see the per-handler comments below for the parity reasoning and pop order.
 */
import toast from 'react-hot-toast';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { ConfirmSheet } from '@/presentation/components/common/sheet/confirm/ConfirmSheet';
import { SheetService } from '@/presentation/components/common/sheet/service/SheetService.index';
import type { l10n } from '@/presentation/locale/l10n';

export interface ChatActionConfirmFlowDeps {
  l10n: l10n;
  /** Current cached predicates the confirm copy branches on. */
  conversationBlocked: boolean;
  userBlocked: boolean;
  muted: boolean;
  /** Peer first name interpolated into the confirm copy. */
  peerFirstName: string;
  /** Hook mutations: each runs the use case and (on success) flips local state. */
  setBlockConversation: (block: boolean) => Promise<boolean>;
  blockUser: (block: boolean) => Promise<boolean>;
  setMute: (mute: boolean) => Promise<boolean>;
  /**
   * Live-reflect callback for the conversation flow. Invoked with the new
   * blocked state only on the success path (the failure path's optimistic write
   * was rolled back). See `ChatActionsSheetContentProps.onConversationBlockChanged`.
   */
  onConversationBlockChanged?: (block: boolean) => void;
  screenKey?: string;
}

export interface ChatActionConfirmFlows {
  handleBlockConversation: () => void;
  handleBlockUser: () => void;
  handleMuteConversation: () => void;
}

/**
 * Build the three confirm-flow handlers from the content component's resolved
 * l10n bindings, the hook mutations, and the peer/screen context. Returned
 * handlers are stable for the lifetime of the values passed in (the component
 * rebuilds them whenever those change).
 */
export function buildChatActionConfirmFlows({
  l10n,
  conversationBlocked,
  userBlocked,
  muted,
  peerFirstName,
  setBlockConversation,
  blockUser,
  setMute,
  onConversationBlockChanged,
  screenKey,
}: ChatActionConfirmFlowDeps): ChatActionConfirmFlows {
  // Block / un-block the conversation (Flutter `_onBlockConversation`,
  // 207-256). Branch the confirm-sheet copy on the *current* state, then on
  // confirm run the mutation, flip the local state, dismiss both stacked sheets
  // (Flutter `_closeBottomSheet()`, line 241), and toast the result. The overlay
  // loading style mirrors Flutter's `_saving` overlay. Two sheets are stacked
  // here (chat-actions underneath, ConfirmSheet on top), and `SheetService.close()`
  // always pops the TOPMOST one: the explicit `SheetService.close()` inside
  // `onConfirm` pops the ConfirmSheet (still topmost at that point), and
  // ConfirmContent's own trailing close (after `onConfirm` settles) then pops the
  // chat-actions sheet beneath. See the inline comment at that `SheetService.close()`
  // call for the full pop order and the invariant this relies on.
  const handleBlockConversation = (): void => {
    // Flutter `willBlock = !_conversationBlocked` — invert the current state.
    const block = !conversationBlocked;

    const title = block
      ? l10n.chat.actions.blockConversationTitle
      : l10n.chat.actions.unblockConversationTitle;
    // `blockConversationDescription` contains `{peerFirstName}` twice (matching
    // Flutter's two `${widget.user.firstName}` interpolations,
    // chat_actions_bottom_sheet.dart:219-220), so replace *all* occurrences —
    // `String.replace` with a string argument only swaps the first.
    const confirmText = (
      block
        ? l10n.chat.actions.blockConversationDescription
        : l10n.chat.actions.unblockConversationDescription
    ).replace(/\{peerFirstName\}/g, peerFirstName);
    const okText = block
      ? l10n.chat.actions.blockConversationConfirm
      : l10n.chat.actions.unblockConversationConfirm;

    void ConfirmSheet.show({
      title,
      confirmText,
      okText,
      cancelText: l10n.common.cancel,
      loadingStyle: 'overlay',
      screenKey,
      onConfirm: async (): Promise<void> => {
        // The hook owns the mutation call and (on success) the local-state flip
        // that mirrors Flutter's next-open Hive re-read.
        const success = await setBlockConversation(block);
        // Dismiss BOTH the ConfirmSheet and the chat-actions sheet beneath it,
        // matching Flutter's `_closeBottomSheet()` on the confirmed path
        // (chat_actions_bottom_sheet.dart:241), regardless of `success`.
        //
        // `SheetService.close()` pops the TOPMOST sheet. At this point the
        // ConfirmSheet is still topmost, so THIS call pops the ConfirmSheet;
        // ConfirmContent's own trailing close (after `onConfirm` settles,
        // ConfirmContent.tsx:47) then pops the chat-actions sheet underneath.
        // Invariant: exactly these two sheets are stacked and ConfirmContent
        // issues exactly one trailing close (it does not auto-close because
        // `closeOnClick` is false while `onConfirm` is set). The toast is a
        // global react-hot-toast surface, not a child of either sheet, so it
        // survives both dismissals.
        //
        // A more robust follow-up would be a single `SheetService.closeAll()`
        // here instead of relying on this two-close coincidence, but that is out
        // of scope for this comment-accuracy fix.
        SheetService.close();
        if (success) {
          // Live-reflect the flip on the actioning user's chat page (composer
          // replacement + app-bar video-call-icon drop). Only on success — the
          // failure path's optimistic write was rolled back by
          // `SetBlockConversationUseCase`, so the page state must stay
          // unchanged (mirrors Flutter reflecting only the confirmed action).
          onConversationBlockChanged?.(block);
          toast.success(l10n.share.success);
        } else {
          toast.error(l10n.chat.actions.blockConversationError);
        }
      },
    });
  };

  // Block / un-block the peer user (Flutter `_onBlockUser`, 258-306). Branch the
  // confirm-sheet copy on the *current* `userBlocked` state, then on confirm run
  // the toggle mutation. The Flutter side-effect asymmetry is reproduced exactly
  // (lines 296-304): on failure → toast the error and LEAVE the sheet open
  // (Flutter does not call `_closeBottomSheet()` on the failure path); on success
  // → close the sheet with NO success toast (Flutter line 303 only calls
  // `_closeBottomSheet()` — unlike `_onBlockConversation`, which toasts "Done").
  // The overlay loading style mirrors Flutter's `_saving` full-screen overlay.
  const handleBlockUser = (): void => {
    // Flutter `willBlock = !_userIsBlocked` — invert the current state.
    const block = !userBlocked;

    // Copy branches on the *current* `userBlocked` state. The web uses separate
    // block/un-block titles (Flutter `_onBlockUser` hard-codes "Block ..." for
    // both — an obvious Flutter bug fixed here per `conventions.md`, matching the
    // already-shipped `userActions` flow). `{peerFirstName}` appears once in the
    // title; replace all occurrences for safety, consistent with
    // `handleBlockConversation`.
    const title = (
      block
        ? l10n.chat.actions.blockUserTitle
        : l10n.chat.actions.unblockUserTitle
    ).replace(/\{peerFirstName\}/g, peerFirstName);
    const confirmText = block
      ? l10n.chat.actions.blockUserDescription
      : l10n.chat.actions.unblockUserDescription;
    const okText = block
      ? l10n.chat.actions.blockUserConfirm
      : l10n.chat.actions.unblockUserConfirm;

    void ConfirmSheet.show({
      title,
      confirmText,
      okText,
      cancelText: l10n.common.cancel,
      // Mirror Flutter's `_saving` full-screen overlay (consistent with
      // `handleBlockConversation`).
      loadingStyle: 'overlay',
      screenKey,
      onConfirm: async (): Promise<void> => {
        // The hook owns the toggle mutation and (on success) the local-state
        // flip that mirrors Flutter's next-open Hive re-read.
        const success = await blockUser(block);
        if (success) {
          // Flutter line 303: success closes the sheet with NO success toast
          // (unlike `_onBlockConversation`). Dismiss BOTH the ConfirmSheet and
          // the chat-actions sheet beneath it — `SheetService.close()` pops the
          // TOPMOST sheet (still the ConfirmSheet here), and ConfirmContent's own
          // trailing close then pops the chat-actions sheet underneath. See the
          // full pop-order note in `handleBlockConversation` above.
          SheetService.close();
        } else {
          // Flutter lines 296-301: failure toasts the error and LEAVES the sheet
          // open (no `_closeBottomSheet()` on the failure path).
          appLogger.error(
            'Failed to block/unblock user',
            'ChatActionsSheetContent.handleBlockUser',
            { context: { willBlock: block } },
          );
          toast.error(l10n.chat.actions.blockUserError);
        }
      },
    });
  };

  // Mute / un-mute the conversation (Flutter `_onMuteConversation`, 308-358).
  // Clones `handleBlockConversation`, substituting the mute use case, l10n keys,
  // and predicate. Branch the confirm-sheet copy on the *current* `muted` state,
  // then on confirm run the mutation, flip the local state, dismiss both stacked
  // sheets (Flutter `_closeBottomSheet()`, line 343), and toast the result.
  // Unlike block, mute has NO chat-page live-reflect side effect in Flutter
  // (no composer/app-bar change), so there is no callback into `ChatPage`.
  const handleMuteConversation = (): void => {
    // Flutter `confirmType == ConfirmType.muteConversation` is true when not
    // currently muted — i.e. the action mutes. Invert the current state.
    const mute = !muted;

    const title = mute
      ? l10n.chat.actions.muteConversationTitle
      : l10n.chat.actions.unmuteConversationTitle;
    // The mute descriptions contain `{peerFirstName}` exactly once (matching
    // Flutter's single `${widget.user.firstName}` interpolation,
    // chat_actions_bottom_sheet.dart:316/322), so the single-occurrence
    // `String.replace` suffices — no `/g` regex (unlike block's two occurrences).
    const confirmText = (
      mute
        ? l10n.chat.actions.muteConversationDescription
        : l10n.chat.actions.unmuteConversationDescription
    ).replace('{peerFirstName}', peerFirstName);
    const okText = mute
      ? l10n.chat.actions.muteConversationConfirm
      : l10n.chat.actions.unmuteConversationConfirm;

    void ConfirmSheet.show({
      title,
      confirmText,
      okText,
      cancelText: l10n.common.cancel,
      loadingStyle: 'overlay',
      screenKey,
      onConfirm: async (): Promise<void> => {
        // The hook owns the mutation call and (on success) the local-state flip
        // that mirrors Flutter's next-open Hive re-read.
        const success = await setMute(mute);
        // Dismiss BOTH the ConfirmSheet and the chat-actions sheet beneath it,
        // matching Flutter's `_closeBottomSheet()` on the confirmed path
        // (chat_actions_bottom_sheet.dart:343), regardless of `success`. See the
        // detailed pop-order/invariant note in `handleBlockConversation` — the
        // explicit close here pops the (topmost) ConfirmSheet and ConfirmContent's
        // own trailing close then pops the chat-actions sheet underneath.
        SheetService.close();
        // Reuse the shared "Done" / error keys (Flutter raises the identical
        // strings for mute and block — no dedicated mute toast keys).
        if (success) {
          toast.success(l10n.share.success);
        } else {
          toast.error(l10n.chat.actions.blockConversationError);
        }
      },
    });
  };

  return { handleBlockConversation, handleBlockUser, handleMuteConversation };
}
